/** Quick views (ALP-71): peeks, side panels, rule health, run timelines, filters. */
export const quick = {
  panel: "Quick view",
  openFull: "Open full page",
  peekHint: "Hover or focus to preview, Enter to open",
  loading: "Loading…",
  notFound: "This item no longer exists.",
  close: "Close",
  copyLink: "Copy link",
  linkCopied: "Link copied",
  preview: "Preview",
  post: {
    title: "Post",
    caption: "Caption",
    publications: "Published on",
    open: "Open post",
    peek: "Post preview",
    noMedia: "No media",
    metrics: "Performance",
    rules: "Rules on this post",
    noRules: "No rules apply to this post.",
    comments_one: "{{count}} comment",
    comments_other: "{{count}} comments",
    viewOnInstagram: "View on Instagram",
    scheduledFor: "Scheduled for {{when}}",
    publishedAgo: "Published {{when}}",
  },
  contact: {
    title: "Person",
    peek: "Person preview",
    firstSeen: "First seen {{when}}",
    lastSeen: "Last active {{when}}",
    tags: "Tags",
    noTags: "No tags yet",
    recent: "Recent activity",
    noActivity: "No comments or messages yet.",
    openProfile: "Open profile",
    identities: "Profiles",
  },
  rule: {
    title: "Rule",
    summary: "In plain words",
    triggerPhrase: {
      comment_created: "someone comments",
      message_received: "someone sends a DM",
      mention: "someone mentions you",
      story_reply: "someone replies to a story",
      postback: "someone taps a button",
    },
    sentence: "When {{trigger}} {{scope}} and {{condition}}, Malfak will: {{actions}}.",
    onThisPost: "on this post",
    onAccount: "on @{{account}}",
    onAllAccounts: "on any account",
    modeAuto: "Malfak does it right away.",
    modeApprove: "Malfak drafts it and waits for your approval.",
    modeSuggest: "Malfak only suggests it; nothing is sent.",
    enabled: "Rule is on",
    disabled: "Rule is off",
    enable: "Turn on",
    disable: "Turn off",
    edit: "Quick edit",
    name: "Name",
    mode: "Mode",
    priority: "Priority",
    perHour: "Max per hour",
    template: "Reply text",
    templateHint: "Variables: {{vars}}",
    save: "Save changes",
    saved: "Rule saved",
    discard: "Discard",
    simulate: "Simulate on this post",
    simulateAll: "Simulate on recent comments",
    simulateHint: "A dry run against this post's recent comments. Nothing is sent.",
    simulateResult: "{{matched}} of {{total}} comments would match",
    noComments: "This post has no comments to test against yet.",
    recentRuns: "Recent runs on this post",
    recentRunsAll: "Recent runs",
    noRuns: "No runs yet.",
    viewAllRuns: "All runs",
    editFull: "Edit in full page",
    stats: "{{matched}} matched · {{executed}} done · {{pending}} pending (7 days)",
    scope: "Applies to",
    scopeAll: "All accounts and posts",
    scopeAccount: "Every post on @{{account}}",
    scopePost: "Only this post",
  },
  health: {
    attention: "Needs attention",
    ok: "Healthy",
    affected: "Affected posts",
    noAffected: "No specific posts.",
    openRuns: "Open the automation log",
    openApprovals: "Review approvals",
    turnOn: "Turn on",
    issues: {
      paused: "Paused: it won't act on new comments or messages.",
      no_matching_posts: "No published post matches its scope, so it can never run.",
      failing_runs_one: "Failed runs in the last 7 days: {{count}}.",
      failing_runs_other: "Failed runs in the last 7 days: {{count}}.",
      approval_backlog_one: "Drafts waiting for approval: {{count}}.",
      approval_backlog_other: "Drafts waiting for approval: {{count}}.",
      account_disconnected: "Its account is disconnected; reconnect it in Settings.",
    },
    matchingPosts_one: "{{count}} post",
    matchingPosts_other: "{{count}} posts",
    allPosts: "All posts",
    dms: "Direct messages",
  },
  run: {
    title: "Run",
    peek: "Run details",
    timeline: "Timeline",
    retry: "Retry",
    retrying: "Retrying…",
    retried: "Retry queued",
    retryDone: "Retry finished",
    retryFailed: "Retry failed",
    retryBulk_one: "Retry ({{count}})",
    retryBulk_other: "Retry ({{count}})",
    retryBulkDone: "{{queued}} queued, {{skipped}} skipped",
    notRetriable: "Only failed runs can be retried.",
    error: "What went wrong",
    actionFailed: "The action failed. Retry may help.",
    retries_one: "Retries: {{count}}",
    retries_other: "Retries: {{count}}",
    retryOf: "Retry of an earlier run",
    steps: {
      event: "What came in",
      conditions: "Checked the conditions",
      ai: "AI read it",
      approval: "Approval",
      action: "What Malfak did",
      provider: "Instagram's answer",
      retry: "Retry",
    },
    stepStatus: { ok: "OK", skipped: "Skipped", pending: "Pending", failed: "Failed", info: "Info" },
    trace: "Open AI trace",
    idempotencyKey: "Idempotency key",
    openEvent: "Open in inbox",
    filters: {
      title: "Filters",
      rule: "Rule",
      post: "Post",
      status: "Status",
      action: "Action",
      time: "Time",
      anyRule: "Any rule",
      anyPost: "Any post",
      anyStatus: "Any status",
      anyAction: "Any action",
      anyTime: "Any time",
      last24h: "Last 24 hours",
      last7d: "Last 7 days",
      last30d: "Last 30 days",
      clear: "Clear filters",
      selected_one: "{{count}} selected",
      selected_other: "{{count}} selected",
      selectRun: "Select run",
      selectAllFailed: "Select failed runs",
    },
  },
  approvals: {
    filters: "Filters",
    post: "Post",
    anyPost: "Any post",
    account: "Account",
    anyAccount: "Any account",
    rule: "Rule",
    anyRule: "Any rule",
    kind: "Kind",
    anyKind: "Any kind",
    age: "Age",
    anyAge: "Any age",
    ageNew: "Under 1 hour",
    ageDay: "1–24 hours",
    ageOld: "Over 24 hours",
    clear: "Clear",
    searchPosts: "Search posts",
    noPosts: "No posts with pending approvals.",
    rejectSelected_one: "Reject ({{count}})",
    rejectSelected_other: "Reject ({{count}})",
    batchRejected_one: "Rejected: {{count}}",
    batchRejected_other: "Rejected: {{count}}",
    batchPartial: "{{ok}} done, {{failed}} failed",
    noMatch: "Nothing matches these filters.",
    onPost: "On this post",
  },
} as const;
